/**
 * Shared API helpers — single source for the API base URL.
 *
 * Import these instead of duplicating in each API module.
 * (The auth-header derivation moved to `utils/persisted-auth` with the #353
 * BFF cutover — OAuth sessions are cookie-based and produce no header.)
 */

/**
 * Derive the API base URL from the current location.
 * Handles reverse proxy prefix (e.g., /everguide/mcp/dashboard).
 */
export function getApiBaseUrl(): string {
  if (typeof window === 'undefined') return '';

  const pathname = window.location.pathname;
  const dashboardIndex = pathname.indexOf('/dashboard');

  if (dashboardIndex > 0) {
    // Return everything before /dashboard (e.g., /everguide/mcp)
    return pathname.substring(0, dashboardIndex);
  }

  // No prefix found, use root
  return '';
}

/**
 * Is `err` an HTTP 401 (Unauthorized)? (#537)
 *
 * Single source of truth for both transports' recovery paths — the MCP SDK
 * surfaces a 401 as `StreamableHTTPError` (`.code === 401`), a raw `Response`
 * carries `.status === 401`, and `apiRequest` throws an `Error` whose message
 * embeds the status (`API request failed: 401 …`). Kept here (a dep-free
 * module) so `mcp-client.ts` and `auth-recovery.ts` can both import it without
 * an import cycle.
 */
export function is401(err: unknown): boolean {
  if (!err) return false;
  const e = err as { code?: unknown; status?: unknown; message?: unknown };
  if (e.code === 401 || e.status === 401) return true;
  const msg = typeof e.message === 'string' ? e.message : String(err);
  return /\b401\b/.test(msg);
}
